import { HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '../services/toast.service';
import { AuthService } from '../services/auth.service';

/** Rutas que NO deben redirigir al login al recibir 401 (son rutas públicas) */
const PUBLIC_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/2fa', '/auth/logout', '/membership/public/plans'];

/** Gate para evitar múltiples mensajes de sesión expirada en peticiones concurrentes */
let is401Showing = false;

/**
 * Interceptor global de errores.
 * En 401: limpia la sesión y redirige al login automáticamente.
 */
export function errorInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const toast = inject(ToastService);
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {

      if (error.status === 401) {
        const isPublicRoute = PUBLIC_ENDPOINTS.some(ep => req.url.includes(ep));

        if (!isPublicRoute) {
          // Sesión expirada → forzar logout limpio sin causar dependencia circular inmediata
          const authService = injector.get(AuthService);
          authService.clearSession();

          if (!is401Showing) {
            is401Showing = true;
            toast.show(
              'Sesión expirada',
              'Tu sesión ha expirado o ya no es válida. Por favor inicia sesión nuevamente.',
              'warning',
              4000,
              'top-right'
            );
            // Reset gate after some time to allow future sessions
            setTimeout(() => is401Showing = false, 3000);
          }
        } else if (!req.url.includes('/auth/logout')) {
          // En login/register mostrar error si no es logout
          toast.show(
            'No autorizado',
            error.error?.message || 'Sus credenciales no son válidas.',
            'error',
            4000,
            'top-right'
          );
        }

      } else if (error.status === 0) {
        toast.show(
          'Error de conexión',
          'No se pudo conectar con el servidor. Verifica tu red.',
          'error',
          4000,
          'top-right'
        );
      } else if (error.status === 403) {
        toast.show(
          'Acceso prohibido',
          'No tienes permisos para realizar esta acción.',
          'error',
          4000,
          'top-right'
        );
      } else if (error.status === 404) {
        toast.show(
          'No encontrado',
          'El recurso solicitado no existe.',
          'info',
          3000,
          'top-right'
        );
      } else if (error.status === 500) {
        toast.show(
          'Error del servidor',
          'El servidor encontró un problema inesperado. Intenta más tarde.',
          'error',
          4000,
          'top-right'
        );
      } else if (error.status !== 400) {
        // 400 los manejan los forms directamente — no mostrar toast genérico
        toast.show(
          'Atención',
          error.error?.message || 'Ocurrió un error inesperado.',
          'error',
          3000,
          'top-right'
        );
      }

      return throwError(() => error);
    })
  );
}

